import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  pickupListing: {
    loading: false,
    error: null,
    data: null,
  },
  pickupDetails: {
    // The pickup id this state describes. Pickup Detail renders nothing that
    // isn't for its own id, and a response for an id it has moved off is
    // dropped — otherwise pickup A's data showed under pickup B's number, and
    // stayed there when B failed with a 403/404.
    requestedId: null,
    loading: false,
    error: null,
    data: null,
  },
  pickupCalendar: {
    loading: false,
    error: null,
    data: null,
  },
  createPickup: {
    loading: false,
    error: null,
    data: null,
  },
  updatePickup: {
    loading: false,
    error: null,
    data: null,
  },
};

// A detail response for a pickup other than the one last requested.
const isStaleDetail = (state, id) =>
  id != null && String(state.pickupDetails.requestedId) !== String(id);

const pickupsSlice = createSlice({
  name: "pickups",
  initialState,
  reducers: {
    // Pickup Listing
    pickupListingStart: (state) => {
      state.pickupListing.loading = true;
      state.pickupListing.error = null;
    },
    pickupListingSuccess: (state, action) => {
      state.pickupListing.loading = false;
      state.pickupListing.data = action.payload;
      state.pickupListing.error = null;
    },
    pickupListingFailure: (state, action) => {
      state.pickupListing.loading = false;
      state.pickupListing.error = action.payload;
    },
    pickupListingReset: (state) => {
      state.pickupListing = initialState.pickupListing;
    },

    // Pickup Details. Start takes the pickup id; success / failure take
    // { id, data } / { id, message } so a late response for a pickup the page
    // has moved off is dropped instead of rendered.
    pickupDetailsStart: (state, action) => {
      const id = action.payload ?? null;
      if (String(state.pickupDetails.requestedId) !== String(id)) {
        // A different pickup: never show the previous one while this loads.
        state.pickupDetails.data = null;
      }
      state.pickupDetails.requestedId = id;
      state.pickupDetails.loading = true;
      state.pickupDetails.error = null;
    },
    pickupDetailsSuccess: (state, action) => {
      const { id, data } = action.payload ?? {};
      if (isStaleDetail(state, id)) return;
      state.pickupDetails.loading = false;
      state.pickupDetails.data = data ?? null;
      state.pickupDetails.error = null;
    },
    pickupDetailsFailure: (state, action) => {
      const { id, message } = action.payload ?? {};
      if (isStaleDetail(state, id)) return;
      state.pickupDetails.loading = false;
      state.pickupDetails.data = null;
      state.pickupDetails.error = message ?? "Request failed";
    },
    pickupDetailsReset: (state) => {
      state.pickupDetails = initialState.pickupDetails;
    },

    // Pickup Calendar
    pickupCalendarStart: (state) => {
      state.pickupCalendar.loading = true;
      state.pickupCalendar.error = null;
    },
    pickupCalendarSuccess: (state, action) => {
      state.pickupCalendar.loading = false;
      state.pickupCalendar.data = action.payload;
      state.pickupCalendar.error = null;
    },
    pickupCalendarFailure: (state, action) => {
      state.pickupCalendar.loading = false;
      state.pickupCalendar.error = action.payload;
    },

    // Create Pickup
    createPickupStart: (state) => {
      state.createPickup.loading = true;
      state.createPickup.error = null;
      state.createPickup.data = null;
    },
    createPickupSuccess: (state, action) => {
      state.createPickup.loading = false;
      state.createPickup.data = action.payload;
      state.createPickup.error = null;
    },
    createPickupFailure: (state, action) => {
      state.createPickup.loading = false;
      state.createPickup.error = action.payload;
    },
    createPickupReset: (state) => {
      state.createPickup = initialState.createPickup;
    },

    // Update Pickup
    updatePickupStart: (state) => {
      state.updatePickup.loading = true;
      state.updatePickup.error = null;
      state.updatePickup.data = null;
    },
    updatePickupSuccess: (state, action) => {
      state.updatePickup.loading = false;
      state.updatePickup.data = action.payload;
      state.updatePickup.error = null;
    },
    updatePickupFailure: (state, action) => {
      state.updatePickup.loading = false;
      state.updatePickup.error = action.payload;
    },
    updatePickupReset: (state) => {
      state.updatePickup = initialState.updatePickup;
    },
  },
});

export const {
  pickupListingStart,
  pickupListingSuccess,
  pickupListingFailure,
  pickupListingReset,
  pickupDetailsStart,
  pickupDetailsSuccess,
  pickupDetailsFailure,
  pickupDetailsReset,
  pickupCalendarStart,
  pickupCalendarSuccess,
  pickupCalendarFailure,
  createPickupStart,
  createPickupSuccess,
  createPickupFailure,
  createPickupReset,
  updatePickupStart,
  updatePickupSuccess,
  updatePickupFailure,
  updatePickupReset,
} = pickupsSlice.actions;

/**
 * The detail state for one pickup id — loading until a response for that id
 * has arrived, so a page never renders another pickup's data (or a stale
 * "not found") under its id.
 */
export const selectPickupDetailsFor = (pickupDetails, id) =>
  pickupDetails &&
  pickupDetails.requestedId != null &&
  String(pickupDetails.requestedId) === String(id)
    ? pickupDetails
    : { requestedId: id ?? null, loading: true, error: null, data: null };

export default pickupsSlice.reducer;
